import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, mergeMap, reduce, switchMap, tap } from 'rxjs/operators';
import { formatFromDate } from 'src/app/common/datetime_utility';
import { getPlatformDisplayName } from 'src/app/common/platformUtils';
import { beautifyCamelCaseString } from 'src/app/common/stringUtils';
import { EmailData } from 'src/app/components/actions/action-send-email/action-send-email.component';
import { ChangeEventJustificationDialogComponent } from 'src/app/components/note-dialog/change-event-justification-dialog.component';
import { IntegrationModel, Platform, ResourceType } from 'src/app/models/api';
import {
  Attachment,
  InventoryApplicationResourceProperties,
  InventoryAutomationResourceProperties,
  InventoryEntityFieldStatus,
  InventoryResourceModel,
  PowerPlatformApplicationQuarantineStatus,
  ResourceStatus,
} from 'src/app/models/api/inventoryResource.model';
import {
  ResetViolationsStatusApiModel,
  UpdatableViolationStatus,
  UpdateViolationsStatusApiModel,
  ViolationChangeJustificationApiModel,
  ViolationModel,
  ViolationsStatusUpdateResponse,
  ViolationStatus,
} from 'src/app/models/api/violations.model';
import { MenuAction } from 'src/app/models/menuAction.model';
import { IChangeEventJustificationComponentOutput } from 'src/app/models/popups/outputs/changeEventJustificationComponentOutput.interface';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import { environment } from 'src/environments/environment';
import {
  ActionResponseModel,
  ActionsHttpServiceService,
  AutomationActivationActions,
  AutomationActivationParams,
  PpApplicationQuarantineStatus,
  RequiredParamsForActionsHttpRequests,
  RequiredParamsForSetAccessHttpRequests,
  RequiredParamsForViolationActionsHttpRequests,
  ResourceActionsResponseModel,
  SetApplicationQuarantineStateParamsModel,
  ViolationsActionsResponseModel,
} from '../../httpServices/actionsHttpService/actions-http-service.service';
import { ViolationsHttpService } from '../../httpServices/violationsHttpService/violations-http.service';
import { PermissionsService } from '../../infra/permissionsService/permissions.service';
import { StoreService } from '../../infra/store/store.service';
import { arePlatformActionsAllowed } from '../common/actionsHelpers';
import { CommonActionsService, EmailPlaceholder } from '../common/common-actions.service';
import { ViolationsSupportedActions } from './violationsSupportedActions';
import { QueryParams } from 'src/app/models/navigation/navigationParams.interface';
import { InventoryHttpService } from '../../httpServices/inventoryHttpService/inventory-http.service';
import { IViolationsViewModel } from 'src/app/models/viewModels/violationsViewModel';
import { JsonHalCollection } from 'src/app/models/api/jsonhal.model';
import { StatusCodeEnum } from 'src/app/models/api/integration.model';
import _ from 'lodash';
import { LoggerService } from '../../infra/logger/logger.service';
import { HttpErrorResponse } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class ViolationsActionsService {
  public readonly haveActions: boolean = true;
  private readonly _violationsEmailPlaceholders: EmailPlaceholder[];
  private _integrations: IntegrationModel[] = [];
  private readonly _actionIdResourceActionModelDictionary: {
    [actionId: string]: ViolationsSupportedActions;
  } = {
    powerplatformQuarantineCanvasApp: ViolationsSupportedActions.Quarantine,
    deletePlatformResource: ViolationsSupportedActions.Delete,
    listResourcePermissions: ViolationsSupportedActions.ListAccess,
    downloadResourceAttachment: ViolationsSupportedActions.GetResourceSbomFile,
    powerplatformStopFlow: ViolationsSupportedActions.Stop,
    getRawResource: ViolationsSupportedActions.GetRawResource,
    powerplatformSetCopilotAuthentication: ViolationsSupportedActions.PowerplatformSetCopilotAuthentication,
    powerplatformGetRawFlowRuns: ViolationsSupportedActions.PowerplatformGetRawFlowRuns,
    powerplatformAgentDeleteAction: ViolationsSupportedActions.PowerplatformAgentDeleteAction,
    powerplatformAgentActionDeleteAction: ViolationsSupportedActions.PowerplatformAgentActionDeleteAction,
    powerplatformAgentActionEnableAction: ViolationsSupportedActions.PowerplatformAgentActionEnableAction,
    powerplatformAgentActionDisableAction: ViolationsSupportedActions.PowerplatformAgentActionDisableAction,
    powerplatformAgentActionSetAuthenticationModeAction:
      ViolationsSupportedActions.PowerplatformAgentActionSetAuthenticationModeAction,
    powerplatformAgentActionSetConfirmationModeAction:
      ViolationsSupportedActions.PowerplatformAgentActionSetConfirmationModeAction,
    powerplatformAgentActionEnforceUserAuthenticationAction:
      ViolationsSupportedActions.PowerplatformAgentActionEnforceUserAuthenticationAction,
    powerplatformAgentActionEnforceUserConfirmationAction:
      ViolationsSupportedActions.PowerplatformAgentActionEnforceUserConfirmationAction,
    powerplatformAgentSetWebBrowsingAction: ViolationsSupportedActions.PowerplatformAgentSetWebBrowsingAction,
    powerplatformEnvironmentSetAuditAction: ViolationsSupportedActions.PowerplatformEnvironmentSetAuditAction,
    chatGptEnterpriseDeleteGptAction: ViolationsSupportedActions.ChatGptEnterpriseDeleteGptAction,
    chatGptEnterpriseIsolateGptAction: ViolationsSupportedActions.ChatGptEnterpriseIsolateGptAction,
    chatGptEnterpriseDeleteGptKnowledgeAction: ViolationsSupportedActions.ChatGptEnterpriseDeleteGptKnowledgeAction,
    chatGptEnterpriseGetGptKnowledgeFileUrlAction:
      ViolationsSupportedActions.ChatGptEnterpriseGetGptKnowledgeFileUrlAction,
    chatGptEnterpriseDeleteUserCanvasAction: ViolationsSupportedActions.ChatGptEnterpriseDeleteUserCanvasAction,
    chatGptEnterpriseGetUserCanvasAction: ViolationsSupportedActions.ChatGptEnterpriseGetUserCanvasAction,
  };
  private readonly _actionIdViolationActionModelDictionary: {
    [actionId: string]: ViolationsSupportedActions;
  } = {
    powerplatformSecureFlowSteps: ViolationsSupportedActions.SecureFlowSteps,
  }; // Currently there are only one action for violation, but we are keeping it as a dictionary for future use

  constructor(
    private readonly _violationsHttpService: ViolationsHttpService,
    private readonly _matDialog: MatDialog,
    private readonly _commonActionsService: CommonActionsService,
    private readonly _permissionsService: PermissionsService,
    private readonly _store: StoreService,
    private readonly _labelProvider: LabelsProviderPipe,
    private readonly _inventoryHttpService: InventoryHttpService,
    private readonly _httpActionsServiceService: ActionsHttpServiceService,
    private readonly _logger: LoggerService
  ) {
    this._store.integrations$.subscribe((integrations: IntegrationModel[]) => {
      this._integrations = integrations;
    });

    this._violationsEmailPlaceholders = [
      {
        label: this._labelProvider.transform('emailTemplatePlaceholders.violationName.label'),
        description: this._labelProvider.transform('emailTemplatePlaceholders.violationName.description'),
      },
      {
        label: this._labelProvider.transform('emailTemplatePlaceholders.violationId.label'),
        description: this._labelProvider.transform('emailTemplatePlaceholders.violationId.description'),
      },
      {
        label: this._labelProvider.transform('emailTemplatePlaceholders.violationSummary.label'),
        description: this._labelProvider.transform('emailTemplatePlaceholders.violationSummary.description'),
      },
      {
        label: this._labelProvider.transform('emailTemplatePlaceholders.violationSeverity.label'),
        description: this._labelProvider.transform('emailTemplatePlaceholders.violationSeverity.description'),
      },
      {
        label: this._labelProvider.transform('emailTemplatePlaceholders.risk.label'),
        description: this._labelProvider.transform('emailTemplatePlaceholders.risk.description'),
      },
      {
        label: this._labelProvider.transform('emailTemplatePlaceholders.triage.label'),
        description: this._labelProvider.transform('emailTemplatePlaceholders.triage.description'),
      },
      {
        label: this._labelProvider.transform('emailTemplatePlaceholders.violationFix.label'),
        description: this._labelProvider.transform('emailTemplatePlaceholders.violationFix.description'),
      },
      {
        label: this._labelProvider.transform('emailTemplatePlaceholders.violationFirstSeen.label'),
        description: this._labelProvider.transform('emailTemplatePlaceholders.violationFirstSeen.description'),
      },
      {
        label: this._labelProvider.transform('emailTemplatePlaceholders.resourceType.label'),
        description: this._labelProvider.transform('emailTemplatePlaceholders.resourceType.description'),
      },
      {
        label: this._labelProvider.transform('emailTemplatePlaceholders.linkToPlatform.label'),
        description: this._labelProvider.transform('emailTemplatePlaceholders.linkToPlatform.description'),
      },
      {
        label: this._labelProvider.transform('emailTemplatePlaceholders.linkToZenity.label'),
        description: this._labelProvider.transform('emailTemplatePlaceholders.linkToZenity.description'),
      },
      {
        label: this._labelProvider.transform('emailTemplatePlaceholders.resourceName.label'),
        description: this._labelProvider.transform('emailTemplatePlaceholders.resourceName.description'),
      },
    ];
  }

  public setActionsForViolations(
    violations: ViolationModel[],
    violationsCurrentPage: IViolationsViewModel[]
  ): Observable<void> {
    const violationsByIntegrationId = _.groupBy(violations, 'integration.id');
    const integrationIds = Object.keys(violationsByIntegrationId);

    if (integrationIds.length === 0) {
      return of(undefined);
    }

    const requests$: Observable<void>[] = integrationIds
      .map((integrationId) => [
        this._httpActionsServiceService
          .getActionsOfResources({
            integrationId: integrationId,
            resourcesIds: violationsByIntegrationId[integrationId].map((violation) => violation.resource.id),
          })
          .pipe(
            tap((response) => {
              this._handleResourcesActionsResponse(integrationId, response, violationsCurrentPage);
            }),
            map(() => undefined),
            catchError((error: HttpErrorResponse) => {
              if (error.status === 403) {
                this._logger.warn(`No permission to get actions of resources for integration ${integrationId}`);
              } else {
                this._logger.error(`Failed to get actions of resources for integration ${integrationId}`, error);
              }
              return of(undefined);
            })
          ),
        this._httpActionsServiceService
          .getActionsOfViolations({
            integrationId: integrationId,
            violationsIds: violationsByIntegrationId[integrationId].map((violation) => violation.id),
          })
          .pipe(
            tap((response) => {
              this._handleViolationsActionsResponse(integrationId, response, violationsCurrentPage);
            }),
            map(() => undefined),
            catchError((error: HttpErrorResponse) => {
              if (error.status === 403) {
                this._logger.warn(`No permission to get actions of violations for integration ${integrationId}`);
              } else {
                this._logger.error(`Failed to get actions of violations for integration ${integrationId}`, error);
              }
              return of(undefined);
            })
          ),
      ])
      .flat();

    return forkJoin(requests$).pipe(map(() => undefined));
  }

  public handleActionClicked(
    action: ViolationsSupportedActions,
    violation: ViolationModel
  ): Observable<ViolationsStatusUpdateResponse | boolean | undefined> {
    const resourceType = violation.resource.resourceType;
    const resourceName = violation.resource.displayName;
    const resourceUrl = violation.resource.platformInfo?.urls?.value?.webAppUrl?.value ?? 'N/A';
    if (action == ViolationsSupportedActions.Exempt) {
      return this._handleViolationStatusChangeActionClicked(violation, UpdatableViolationStatus.exempted);
    }
    if (action == ViolationsSupportedActions.Waive) {
      return this._handleViolationStatusResetActionClicked(violation);
    }
    if (action == ViolationsSupportedActions.SendEmail) {
      return this._commonActionsService.sendEmailAction(this._createEmailData(violation));
    }

    const actionsHttpParams = {
      integrationId: violation.integration.id,
      resourceId: violation.resource.id,
      resourceType: resourceType,
      resourceStatus: violation.resource.resourceState?.status,
    } as RequiredParamsForActionsHttpRequests;

    const actionOptionalParams = { platformUrl: resourceUrl };

    if (action == ViolationsSupportedActions.AddOwner) {
      return this._commonActionsService.addOrRemoveOwnerAction(actionsHttpParams, true, actionOptionalParams);
    }
    if (action == ViolationsSupportedActions.RemoveOwner) {
      return this._commonActionsService.addOrRemoveOwnerAction(actionsHttpParams, false, actionOptionalParams);
    }
    if (action == ViolationsSupportedActions.SetAccess) {
      const params = {
        ...actionsHttpParams,
        isChangeAccessActionEnabled: true,
      } as RequiredParamsForSetAccessHttpRequests;
      return this._commonActionsService.setAccessAction(params, actionOptionalParams);
    }
    if (action == ViolationsSupportedActions.ListAccess) {
      const params = {
        ...actionsHttpParams,
        isChangeAccessActionEnabled: false,
      } as RequiredParamsForSetAccessHttpRequests;
      return this._commonActionsService.setAccessAction(params, actionOptionalParams);
    }
    if (action == ViolationsSupportedActions.Delete) {
      return this._commonActionsService.deleteAction(resourceName, actionsHttpParams);
    }
    if (action === ViolationsSupportedActions.SecureFlowSteps) {
      const violation_actions_http_params = {
        violationId: violation.id,
        ...actionsHttpParams,
      } as RequiredParamsForViolationActionsHttpRequests;
      return this._commonActionsService.secureFlowSteps(violation_actions_http_params);
    }
    if (action == ViolationsSupportedActions.Quarantine || action == ViolationsSupportedActions.Unquarantine) {
      const params = {
        ...actionsHttpParams,
        quarantine_status:
          action == ViolationsSupportedActions.Quarantine
            ? PpApplicationQuarantineStatus.quarantined
            : PpApplicationQuarantineStatus.unquarantined,
      } as SetApplicationQuarantineStateParamsModel;
      return this._commonActionsService.setResourceQuarantineStateAction(resourceName, params);
    }
    if (action === ViolationsSupportedActions.Start || action == ViolationsSupportedActions.Stop) {
      if (resourceType === ResourceType.recipe || resourceType === ResourceType.flow) {
        const params = {
          ...actionsHttpParams,
          action:
            action === ViolationsSupportedActions.Start
              ? AutomationActivationActions.start
              : AutomationActivationActions.stop,
        } as AutomationActivationParams;
        return this._commonActionsService.automationActivation(params, resourceType);
      }
    }
    if (action == ViolationsSupportedActions.GetResourceDefinition) {
      return this._commonActionsService.showRawData(actionsHttpParams);
    }

    if (action == ViolationsSupportedActions.GetResourceSbomFile) {
      try {
        this._inventoryHttpService
          .getInventoryResource(violation.integration.id, violation.resource.id)
          .pipe(
            map((resource: InventoryResourceModel) => {
              const attachments = resource?.attachments ?? [];
              const sbomAttachment = this._commonActionsService.findSbomAttachment(attachments);
              if (!sbomAttachment) {
                // Since SBOM action shouldnt be available if there is no SBOM attachment, this should never happen
                throw new Error(
                  `SBOM attachment not found for action: '${action}'. ${attachments.length} attachments found`
                );
              }
              return sbomAttachment;
            }),
            switchMap((sbomAttachment: Attachment) =>
              this._commonActionsService.getResourceAttachment(actionsHttpParams, sbomAttachment.id)
            )
          )
          .subscribe();
      } catch {
        this._logger.error(`ViolationsActionsService handleActionClicked: ${action} failed.`);
        return of(false);
      }
      return of(true);
    }
    if (action == ViolationsSupportedActions.GetRawResource) {
      return this._commonActionsService.showRawData(actionsHttpParams);
    }
    if (action == ViolationsSupportedActions.PowerplatformSetCopilotAuthentication) {
      return this._commonActionsService.powerplatformSetCopilotAuthenticationAction(actionsHttpParams);
    }
    if (action == ViolationsSupportedActions.PowerplatformGetRawFlowRuns) {
      return this._commonActionsService.OpenShowFlowRunsConfirmationDialogDialog(actionsHttpParams);
    }
    if (action === ViolationsSupportedActions.PowerplatformAgentActionDeleteAction) {
      return this._commonActionsService.powerplatformAgentActionDeleteAction(resourceName, actionsHttpParams);
    }
    if (action === ViolationsSupportedActions.PowerplatformAgentActionSetStatusAction) {
      return this._commonActionsService.powerplatformAgentActionSetActionStatusDialog(actionsHttpParams);
    }
    if (action === ViolationsSupportedActions.PowerplatformAgentActionSetAuthenticationModeAction) {
      return this._commonActionsService.powerplatformAgentActionSetAuthenticationModeDialog(actionsHttpParams);
    }
    if (action === ViolationsSupportedActions.PowerplatformAgentActionSetConfirmationModeAction) {
      return this._commonActionsService.powerplatformAgentActionSetConfirmationModeDialog(actionsHttpParams);
    }
    if (action === ViolationsSupportedActions.PowerplatformAgentDeleteAction) {
      return this._commonActionsService.powerplatformAgentDeleteAction(resourceName, actionsHttpParams);
    }
    if (action === ViolationsSupportedActions.PowerplatformAgentActionEnforceUserAuthenticationAction) {
      return this._commonActionsService.powerplatformAgentActionEnforceUserAuthenticationAction(
        resourceName,
        actionsHttpParams
      );
    }
    if (action === ViolationsSupportedActions.PowerplatformAgentActionEnforceUserConfirmationAction) {
      return this._commonActionsService.powerplatformAgentActionEnforceUserConfirmationAction(
        resourceName,
        actionsHttpParams
      );
    }
    if (action === ViolationsSupportedActions.PowerplatformAgentSetWebBrowsingAction) {
      return this._commonActionsService.powerplatformAgentActionSetWebBrowsingDialog(actionsHttpParams);
    }
    if (action === ViolationsSupportedActions.PowerplatformEnvironmentSetAuditAction) {
      return this._commonActionsService.powerplatformEnvironmentSetAuditDialog(actionsHttpParams);
    }
    if (action == ViolationsSupportedActions.ChatGptEnterpriseDeleteGptAction) {
      return this._commonActionsService.chatGptEnterpriseDeleteGptAction(resourceName, actionsHttpParams);
    }
    if (action == ViolationsSupportedActions.ChatGptEnterpriseIsolateGptAction) {
      return this._commonActionsService.chatGptEnterpriseIsolateGptAction(actionsHttpParams);
    }
    if (action == ViolationsSupportedActions.ChatGptEnterpriseGetGptKnowledgeFileUrlAction) {
      return this._commonActionsService.chatGptEnterpriseGetGptKnowledgeFileUrlAction(resourceName, actionsHttpParams);
    }
    if (action == ViolationsSupportedActions.ChatGptEnterpriseGetUserCanvasAction) {
      return this._commonActionsService.chatGptEnterpriseGetUserCanvasAction(resourceName, actionsHttpParams);
    }

    throw new Error(`ViolationsActionsService handleActionClicked: ${action} support not implemented.`);
  }

  public getViolationEmailPlaceholders(): EmailPlaceholder[] {
    return this._violationsEmailPlaceholders;
  }

  private _handleResourcesActionsResponse(
    integrationId: string,
    response: JsonHalCollection<ResourceActionsResponseModel>,
    violationsCurrentPage: IViolationsViewModel[]
  ): void {
    const resourcesActions = response._embedded.items;
    const resourcesIdsActionsDict: {
      [resourceId: string]: ResourceActionsResponseModel;
    } = {};
    resourcesActions.forEach((resourceActions) => {
      resourcesIdsActionsDict[resourceActions.resourceId] = resourceActions;
    });
    violationsCurrentPage.forEach((violation) => {
      if (!violation.menuActions) violation.menuActions = [];
      if (violation.integrationId == integrationId && violation.violation.resource.id in resourcesIdsActionsDict) {
        const newActions = this._getMenuActionForResource(
          violation,
          resourcesIdsActionsDict[violation.violation.resource.id].actions
        );

        this._addMenuAction(violation.menuActions, newActions);
      }
    });
  }

  private _handleViolationsActionsResponse(
    integrationId: string,
    response: JsonHalCollection<ViolationsActionsResponseModel>,
    violationsCurrentPage: IViolationsViewModel[]
  ): void {
    const violationsActions = response._embedded.items;
    const violationsIdsActionsDict: {
      [resourceId: string]: ViolationsActionsResponseModel;
    } = {};
    violationsActions.forEach((violationActions) => {
      violationsIdsActionsDict[violationActions.violationId] = violationActions;
    });
    violationsCurrentPage.forEach((violation) => {
      if (!violation.menuActions) violation.menuActions = [];
      if (violation.integrationId == integrationId && violation.violation.id in violationsIdsActionsDict) {
        const newActions = this._getMenuActionForViolation(
          violation,
          violationsIdsActionsDict[violation.violation.id].actions
        );

        this._addMenuAction(violation.menuActions, newActions);
      }
    });
  }

  private _getMenuActionForResource(violationView: IViolationsViewModel, actions: ActionResponseModel[]): MenuAction[] {
    const violation = violationView.violation;
    const resourceType = violation.resource.resourceType;
    const resourceStatus = violation.resource.resourceState?.status;
    const integrationId = violation.integration.id;
    const newActions: MenuAction[] = [];
    if (violation.status !== ViolationStatus.resolved) {
      if (violation.status != ViolationStatus.exempted) {
        newActions.push(this._getMenuAction(ViolationsSupportedActions.Exempt, resourceType, undefined, integrationId));
      }
      if (violation.status != ViolationStatus.open) {
        newActions.push(this._getMenuAction(ViolationsSupportedActions.Waive, resourceType, undefined, integrationId));
      }
      actions.forEach((action) => {
        let flag = true;
        const actionModel = this._actionIdResourceActionModelDictionary[action.id];
        if (actionModel) {
          if (actionModel == ViolationsSupportedActions.Quarantine) {
            const application_properties = violation.resource
              ?.resourceCategoryProperties as InventoryApplicationResourceProperties;
            if (
              application_properties !== undefined &&
              application_properties.quarantineStatus.status === InventoryEntityFieldStatus.hasValue
            ) {
              const newAction =
                application_properties.quarantineStatus.value ===
                PowerPlatformApplicationQuarantineStatus.Unquarantined.toString()
                  ? ViolationsSupportedActions.Quarantine
                  : ViolationsSupportedActions.Unquarantine;
              newActions.push(
                this._getMenuAction(newAction, resourceType, resourceStatus, integrationId, action.requiredPermissions)
              );
            }
            flag = false;
          }
          if (actionModel == ViolationsSupportedActions.Stop) {
            const flowProperties = violation.resource
              ?.resourceCategoryProperties as InventoryAutomationResourceProperties;
            // do not allow start/stop on suspended flow
            if (
              flowProperties.automationStatus.status == InventoryEntityFieldStatus.hasValue &&
              flowProperties.automationStatus.value !== 'Suspended'
            ) {
              const status = resourceStatus;
              const newAction =
                status == ResourceStatus.active ? ViolationsSupportedActions.Stop : ViolationsSupportedActions.Start;
              newActions.push(
                this._getMenuAction(newAction, resourceType, resourceStatus, integrationId, action.requiredPermissions)
              );
            }
            flag = false;
          }
          if (actionModel == ViolationsSupportedActions.ListAccess) {
            const removeAccess = 'removeResourcePermissions';
            const actionsIds = actions.map((a) => a.id);
            if (actionsIds.includes(removeAccess)) {
              newActions.push(
                this._getMenuAction(
                  ViolationsSupportedActions.SetAccess,
                  resourceType,
                  resourceStatus,
                  integrationId,
                  action.requiredPermissions
                )
              );
            } else {
              newActions.push(
                this._getMenuAction(
                  ViolationsSupportedActions.ListAccess,
                  resourceType,
                  resourceStatus,
                  integrationId,
                  action.requiredPermissions
                )
              );
            }
            flag = false;
          }
          if (
            actionModel === ViolationsSupportedActions.PowerplatformAgentActionEnableAction ||
            actionModel === ViolationsSupportedActions.PowerplatformAgentActionDisableAction
          ) {
            flag = false;
          }
          if (flag) {
            newActions.push(
              this._getMenuAction(
                this._actionIdResourceActionModelDictionary[action.id],
                resourceType,
                resourceStatus,
                integrationId,
                action.requiredPermissions
              )
            );
          }
        }
      });

      const enableAction = actions.find(
        (a) => a.id === ViolationsSupportedActions.PowerplatformAgentActionEnableAction
      );
      const disableAction = actions.find(
        (a) => a.id === ViolationsSupportedActions.PowerplatformAgentActionDisableAction
      );

      if (enableAction || disableAction) {
        const statusActionRequiredPermissions = enableAction?.requiredPermissions || disableAction?.requiredPermissions;
        newActions.push(
          this._getMenuAction(
            ViolationsSupportedActions.PowerplatformAgentActionSetStatusAction,
            resourceType,
            resourceStatus,
            integrationId,
            statusActionRequiredPermissions
          )
        );
      }

      // Our new API doesn't support all Powerplatform platform actions yet. Thus, we are taking the old code.
      if (violation.resource.platformInfo.platform == Platform.powerplatform) {
        if (this._commonActionsService.supportedAddOwnersResourceTypesArray.includes(resourceType)) {
          newActions.push(
            this._getMenuAction(ViolationsSupportedActions.AddOwner, resourceType, resourceStatus, integrationId)
          );
        }
        if (this._commonActionsService.supportedRemoveOwnersResourceTypesArray.includes(resourceType)) {
          newActions.push(
            this._getMenuAction(ViolationsSupportedActions.RemoveOwner, resourceType, resourceStatus, integrationId)
          );
        }
        if (this._commonActionsService.isGetDefinitionOptionVisible(Platform.powerplatform, resourceType)) {
          if (integrationId) {
            this._commonActionsService.isGetDefinitionOptionEnabled(integrationId).subscribe((value) => {
              const disabledReason = value?.disabledReason;
              const action = {
                name: ViolationsSupportedActions.GetResourceDefinition.toString(),
                displayNameLabel:
                  resourceType == ResourceType.flow
                    ? 'commonActions.getFlowDefinition'
                    : 'commonActions.getResourceDefinition',
                disabled: disabledReason != undefined,
                tooltip: disabledReason,
              };
              newActions.push(action);
            });
          }
        }
      }
      // Our new API doesn't support Workato platform actions yet. Thus, we are taking the old code.
      if (violation.resource.platformInfo.platform == Platform.workato) {
        const integration = this._integrations.filter((integration) => integration.id == violation.integration.id);
        if (
          integration.length == 1 &&
          arePlatformActionsAllowed(Platform.workato, integration[0].authenticationMethod)
        ) {
          if (resourceType === ResourceType.recipe) {
            const action =
              resourceStatus === ResourceStatus.active
                ? ViolationsSupportedActions.Stop
                : ViolationsSupportedActions.Start;
            newActions.push(this._getMenuAction(action, resourceType, resourceStatus, integrationId));

            newActions.push(
              this._getMenuAction(ViolationsSupportedActions.Delete, resourceType, resourceStatus, integrationId)
            );
          }
        }
      }
    }
    newActions.push(this._getMenuAction(ViolationsSupportedActions.SendEmail, resourceType, undefined, integrationId));
    return newActions;
  }

  private _getMenuActionForViolation(
    violationView: IViolationsViewModel,
    actions: ActionResponseModel[]
  ): MenuAction[] {
    const violation = violationView.violation;
    const resourceType = violation.resource.resourceType;
    const resourceStatus = violation.resource.resourceState?.status;
    const integrationId = violation.integration.id;
    const newActions: MenuAction[] = [];
    if (violation.status !== ViolationStatus.resolved) {
      actions.forEach((action) => {
        const actionModel = this._actionIdViolationActionModelDictionary[action.id];
        if (actionModel && actionModel == ViolationsSupportedActions.SecureFlowSteps) {
          const integration = this._integrations.find((i) => i.id == integrationId);
          if (integration?.connectionStatus.code == StatusCodeEnum.on) {
            const newAction = this._getMenuAction(
              ViolationsSupportedActions.SecureFlowSteps,
              resourceType,
              resourceStatus,
              integrationId,
              action.requiredPermissions
            );
            newActions.push(newAction);
          }
        }
      });
    }
    return newActions;
  }

  private _addMenuAction(menuActions: MenuAction[], menuActions2: MenuAction[]): void {
    const actionsNamesToAddInTheEnd: string[] = [ViolationsSupportedActions.SecureFlowSteps.toString()];
    const actionsToAddInTheEnd: MenuAction[] = [];
    menuActions.forEach((action) => {
      if (actionsNamesToAddInTheEnd.includes(action.name)) {
        actionsToAddInTheEnd.push(action);
        menuActions.splice(menuActions.indexOf(action), 1);
      }
    });

    menuActions2.forEach((action) => {
      if (!menuActions.find((menuAction) => menuAction.name == action.name)) {
        menuActions.push(action);
      }
    });
    actionsToAddInTheEnd.forEach((action) => {
      menuActions.push(action);
    });
  }

  private _handleViolationStatusChangeActionClicked(
    violation: ViolationModel,
    newStatus: UpdatableViolationStatus
  ): Observable<ViolationsStatusUpdateResponse | undefined> {
    return this._openChangeEventJustificationDialog().pipe(
      mergeMap((output: IChangeEventJustificationComponentOutput) => {
        if (output.addJustification && output.content) {
          const updateModel = {
            ids: [violation.id],
            status: newStatus,
            change_reason: {
              justification: output.content,
            } as ViolationChangeJustificationApiModel,
          } as UpdateViolationsStatusApiModel;
          return this._violationsHttpService
            .updateViolationStatus(violation.integration.id, updateModel)
            .pipe(reduce((data) => data));
        }
        return of(undefined);
      })
    );
  }

  private _handleViolationStatusResetActionClicked(
    violation: ViolationModel
  ): Observable<ViolationsStatusUpdateResponse | undefined> {
    return this._openChangeEventJustificationDialog().pipe(
      mergeMap((output: IChangeEventJustificationComponentOutput) => {
        if (output.addJustification && output.content) {
          const resetModel = {
            ids: [violation.id],
            reset_reason: {
              justification: output.content,
            } as ViolationChangeJustificationApiModel,
          } as ResetViolationsStatusApiModel;
          return this._violationsHttpService.resetViolationStatus(violation.integration.id, resetModel);
        }
        return of(undefined);
      })
    );
  }

  private _getMenuAction(
    supportedAction: ViolationsSupportedActions,
    resourceType: ResourceType,
    inventoryResourceStatus?: ResourceStatus,
    integrationId?: string,
    requiredPermissions?: string[]
  ): MenuAction {
    let hasPermissions;
    const isDeleted = this._isResourceDeleted(inventoryResourceStatus);
    const entity = 'violation';

    // Helper for resource-specific permission check
    const getResourceActionRole = (): string =>
      this._permissionsService.extractRoleFromPermissionEnum(
        this._commonActionsService.getPermissionRequiredForResourceAction(resourceType)
      );

    switch (supportedAction) {
      case ViolationsSupportedActions.Exempt:
        hasPermissions = this._permissionsService.checkActionPermission(
          entity,
          'operator',
          integrationId,
          requiredPermissions
        );
        return {
          name: ViolationsSupportedActions.Exempt.toString(),
          displayNameLabel: 'violationActions.exempt',
          disabled: !hasPermissions,
          tooltip: !hasPermissions ? this._labelProvider.transform('commonActions.operatorActionTooltip') : '',
        } as MenuAction;
      case ViolationsSupportedActions.Waive:
        hasPermissions = this._permissionsService.checkActionPermission(
          entity,
          'operator',
          integrationId,
          requiredPermissions
        );
        return {
          name: ViolationsSupportedActions.Waive.toString(),
          displayNameLabel: 'violationActions.waive',
          disabled: !hasPermissions,
          tooltip: !hasPermissions ? this._labelProvider.transform('commonActions.operatorActionTooltip') : '',
        } as MenuAction;
      case ViolationsSupportedActions.AddOwner:
        hasPermissions = this._permissionsService.checkActionPermission(
          entity,
          getResourceActionRole(),
          integrationId,
          requiredPermissions
        );
        return {
          name: ViolationsSupportedActions.AddOwner.toString(),
          displayNameLabel: 'commonActions.addOwner',
          disabled: !hasPermissions || isDeleted,
          tooltip: !hasPermissions
            ? this._labelProvider.transform('commonActions.adminActionTooltip')
            : isDeleted
              ? this._labelProvider.transform('commonActions.deletedResourceTooltip')
              : '',
        } as MenuAction;
      case ViolationsSupportedActions.RemoveOwner:
        hasPermissions = this._permissionsService.checkActionPermission(
          entity,
          getResourceActionRole(),
          integrationId,
          requiredPermissions
        );
        return {
          name: ViolationsSupportedActions.RemoveOwner.toString(),
          displayNameLabel: 'commonActions.removeOwner',
          disabled: !hasPermissions || isDeleted,
          tooltip: !hasPermissions
            ? this._labelProvider.transform('commonActions.adminActionTooltip')
            : isDeleted
              ? this._labelProvider.transform('commonActions.deletedResourceTooltip')
              : '',
        } as MenuAction;
      case ViolationsSupportedActions.ListAccess:
        hasPermissions =
          requiredPermissions && integrationId
            ? this._permissionsService.hasAnyRequiredPermission(requiredPermissions, integrationId)
            : true;
        return {
          name: ViolationsSupportedActions.ListAccess.toString(),
          displayNameLabel: 'commonActions.setAccess',
          disabled: !hasPermissions || isDeleted,
        } as MenuAction;
      case ViolationsSupportedActions.SetAccess:
        hasPermissions =
          requiredPermissions && integrationId
            ? this._permissionsService.hasAnyRequiredPermission(requiredPermissions, integrationId)
            : true;
        return {
          name: ViolationsSupportedActions.SetAccess.toString(),
          displayNameLabel: 'commonActions.setAccess',
          disabled: !hasPermissions || isDeleted,
        };
      case ViolationsSupportedActions.Delete:
        hasPermissions = this._permissionsService.checkActionPermission(
          entity,
          'admin',
          integrationId,
          requiredPermissions
        );
        return {
          name: ViolationsSupportedActions.Delete.toString(),
          displayNameLabel: 'commonActions.deleteResource',
          disabled: !hasPermissions || isDeleted,
          tooltip: !hasPermissions
            ? this._labelProvider.transform('commonActions.adminActionTooltip')
            : isDeleted
              ? this._labelProvider.transform('commonActions.deletedResourceTooltip')
              : '',
        } as MenuAction;
      case ViolationsSupportedActions.SendEmail:
        hasPermissions = this._permissionsService.checkActionPermission(
          entity,
          'operator',
          integrationId,
          requiredPermissions
        );
        return {
          name: ViolationsSupportedActions.SendEmail.toString(),
          displayNameLabel: 'commonActions.sendEmail',
          disabled: !hasPermissions,
          tooltip: !hasPermissions ? this._labelProvider.transform('commonActions.operatorActionTooltip') : '',
        } as MenuAction;
      case ViolationsSupportedActions.Quarantine:
        hasPermissions = this._permissionsService.checkActionPermission(
          entity,
          'operator',
          integrationId,
          requiredPermissions
        );
        return {
          name: ViolationsSupportedActions.Quarantine.toString(),
          displayNameLabel: 'commonActions.quarantine',
          disabled: !hasPermissions,
          tooltip: !hasPermissions ? this._labelProvider.transform('commonActions.operatorActionTooltip') : '',
        } as MenuAction;
      case ViolationsSupportedActions.Unquarantine:
        hasPermissions = this._permissionsService.checkActionPermission(
          entity,
          getResourceActionRole(),
          integrationId,
          requiredPermissions
        );
        return {
          name: ViolationsSupportedActions.Unquarantine.toString(),
          displayNameLabel: 'commonActions.unquarantine',
          disabled: !hasPermissions || isDeleted,
          tooltip: !hasPermissions
            ? this._labelProvider.transform('commonActions.adminActionTooltip')
            : isDeleted
              ? this._labelProvider.transform('commonActions.deletedResourceTooltip')
              : '',
        } as MenuAction;
      case ViolationsSupportedActions.Start:
        hasPermissions = this._permissionsService.checkActionPermission(
          entity,
          'operator',
          integrationId,
          requiredPermissions
        );
        return {
          name: ViolationsSupportedActions.Start.toString(),
          displayNameLabel: 'commonActions.start',
          disabled: !hasPermissions,
          tooltip: !hasPermissions ? this._labelProvider.transform('commonActions.operatorActionTooltip') : '',
        } as MenuAction;
      case ViolationsSupportedActions.Stop:
        hasPermissions = this._permissionsService.checkActionPermission(
          entity,
          'operator',
          integrationId,
          requiredPermissions
        );
        return {
          name: ViolationsSupportedActions.Stop.toString(),
          displayNameLabel: 'commonActions.stop',
          disabled: !hasPermissions,
          tooltip: !hasPermissions ? this._labelProvider.transform('commonActions.operatorActionTooltip') : '',
        } as MenuAction;
      case ViolationsSupportedActions.SecureFlowSteps:
        hasPermissions = this._permissionsService.checkActionPermission(
          entity,
          'operator',
          integrationId,
          requiredPermissions
        );
        return {
          name: ViolationsSupportedActions.SecureFlowSteps.toString(),
          displayNameLabel: 'commonActions.secureFlowSteps',
          disabled: !hasPermissions,
          tooltip: !hasPermissions ? this._labelProvider.transform('commonActions.operatorActionTooltip') : '',
        } as MenuAction;
      case ViolationsSupportedActions.GetResourceSbomFile:
        hasPermissions =
          requiredPermissions && integrationId
            ? this._permissionsService.hasAnyRequiredPermission(requiredPermissions, integrationId)
            : true;
        return {
          name: ViolationsSupportedActions.GetResourceSbomFile.toString(),
          displayNameLabel: 'commonActions.getResourceSbomFile',
          disabled: !hasPermissions || isDeleted,
        } as MenuAction;
      case ViolationsSupportedActions.GetRawResource:
        hasPermissions =
          requiredPermissions && integrationId
            ? this._permissionsService.hasAnyRequiredPermission(requiredPermissions, integrationId)
            : true;
        return {
          name: ViolationsSupportedActions.GetRawResource.toString(),
          displayNameLabel: 'commonActions.getRawResourceAction.actionName',
          disabled: !hasPermissions || isDeleted,
        } as MenuAction;
      case ViolationsSupportedActions.PowerplatformSetCopilotAuthentication:
        hasPermissions =
          requiredPermissions && integrationId
            ? this._permissionsService.hasAnyRequiredPermission(requiredPermissions, integrationId)
            : true;
        return {
          name: ViolationsSupportedActions.PowerplatformSetCopilotAuthentication.toString(),
          displayNameLabel: 'commonActions.powerplatformSetCopilotAuthentication.actionName',
          disabled: !hasPermissions || isDeleted,
        } as MenuAction;
      case ViolationsSupportedActions.PowerplatformGetRawFlowRuns:
        hasPermissions =
          requiredPermissions && integrationId
            ? this._permissionsService.hasAnyRequiredPermission(requiredPermissions, integrationId)
            : true;
        return {
          name: ViolationsSupportedActions.PowerplatformGetRawFlowRuns.toString(),
          displayNameLabel: 'commonActions.getFlowRunsAction.displayName',
          disabled: !hasPermissions || isDeleted,
        } as MenuAction;
      case ViolationsSupportedActions.PowerplatformAgentDeleteAction:
        hasPermissions =
          requiredPermissions && integrationId
            ? this._permissionsService.hasAnyRequiredPermission(requiredPermissions, integrationId)
            : true;
        return {
          name: ViolationsSupportedActions.PowerplatformAgentDeleteAction.toString(),
          displayNameLabel: 'commonActions.deleteResource',
          disabled: !hasPermissions || isDeleted,
        } as MenuAction;
      case ViolationsSupportedActions.PowerplatformAgentActionDeleteAction:
        hasPermissions =
          requiredPermissions && integrationId
            ? this._permissionsService.hasAnyRequiredPermission(requiredPermissions, integrationId)
            : true;
        return {
          name: ViolationsSupportedActions.PowerplatformAgentActionDeleteAction.toString(),
          displayNameLabel: 'commonActions.deleteResource',
          disabled: !hasPermissions || isDeleted,
        } as MenuAction;
      case ViolationsSupportedActions.PowerplatformAgentActionSetStatusAction:
        hasPermissions =
          requiredPermissions && integrationId
            ? this._permissionsService.hasAnyRequiredPermission(requiredPermissions, integrationId)
            : true;
        return {
          name: ViolationsSupportedActions.PowerplatformAgentActionSetStatusAction.toString(),
          displayNameLabel: 'commonActions.actionStatus.label',
          disabled: !hasPermissions || isDeleted,
        } as MenuAction;
      case ViolationsSupportedActions.PowerplatformAgentActionSetAuthenticationModeAction:
        hasPermissions =
          requiredPermissions && integrationId
            ? this._permissionsService.hasAnyRequiredPermission(requiredPermissions, integrationId)
            : true;
        return {
          name: ViolationsSupportedActions.PowerplatformAgentActionSetAuthenticationModeAction.toString(),
          displayNameLabel: 'commonActions.authenticationMode.label',
          disabled: !hasPermissions || isDeleted,
        } as MenuAction;
      case ViolationsSupportedActions.PowerplatformAgentActionSetConfirmationModeAction:
        hasPermissions =
          requiredPermissions && integrationId
            ? this._permissionsService.hasAnyRequiredPermission(requiredPermissions, integrationId)
            : true;
        return {
          name: ViolationsSupportedActions.PowerplatformAgentActionSetConfirmationModeAction.toString(),
          displayNameLabel: 'commonActions.confirmationMode.label',
          disabled: !hasPermissions || isDeleted,
        } as MenuAction;
      case ViolationsSupportedActions.PowerplatformAgentActionEnforceUserAuthenticationAction:
        hasPermissions =
          requiredPermissions && integrationId
            ? this._permissionsService.hasAnyRequiredPermission(requiredPermissions, integrationId)
            : true;
        return {
          name: ViolationsSupportedActions.PowerplatformAgentActionEnforceUserAuthenticationAction.toString(),
          displayNameLabel: 'commonActions.agentActionEnforceUserAuthentication',
          disabled: !hasPermissions || isDeleted,
        } as MenuAction;
      case ViolationsSupportedActions.PowerplatformAgentActionEnforceUserConfirmationAction:
        hasPermissions =
          requiredPermissions && integrationId
            ? this._permissionsService.hasAnyRequiredPermission(requiredPermissions, integrationId)
            : true;
        return {
          name: ViolationsSupportedActions.PowerplatformAgentActionEnforceUserConfirmationAction.toString(),
          displayNameLabel: 'commonActions.agentActionEnforceUserConfirmation',
          disabled: !hasPermissions || isDeleted,
        } as MenuAction;
      case ViolationsSupportedActions.PowerplatformAgentSetWebBrowsingAction:
        hasPermissions =
          requiredPermissions && integrationId
            ? this._permissionsService.hasAnyRequiredPermission(requiredPermissions, integrationId)
            : true;
        return {
          name: ViolationsSupportedActions.PowerplatformAgentSetWebBrowsingAction.toString(),
          displayNameLabel: 'commonActions.webBrowsing.label',
          disabled: !hasPermissions || isDeleted,
        } as MenuAction;
      case ViolationsSupportedActions.PowerplatformEnvironmentSetAuditAction:
        hasPermissions =
          requiredPermissions && integrationId
            ? this._permissionsService.hasAnyRequiredPermission(requiredPermissions, integrationId)
            : true;
        return {
          name: ViolationsSupportedActions.PowerplatformEnvironmentSetAuditAction.toString(),
          displayNameLabel: 'commonActions.environmentAudit.label',
          disabled: !hasPermissions || isDeleted,
        } as MenuAction;
      case ViolationsSupportedActions.ChatGptEnterpriseDeleteGptAction:
        hasPermissions =
          requiredPermissions && integrationId
            ? this._permissionsService.hasAnyRequiredPermission(requiredPermissions, integrationId)
            : true;
        return {
          name: ViolationsSupportedActions.ChatGptEnterpriseDeleteGptAction.toString(),
          displayNameLabel: 'commonActions.deleteResource',
          disabled: !hasPermissions || isDeleted,
        } as MenuAction;
      case ViolationsSupportedActions.ChatGptEnterpriseIsolateGptAction:
        hasPermissions =
          requiredPermissions && integrationId
            ? this._permissionsService.hasAnyRequiredPermission(requiredPermissions, integrationId)
            : true;
        return {
          name: ViolationsSupportedActions.ChatGptEnterpriseIsolateGptAction.toString(),
          displayNameLabel: 'commonActions.isolate',
          disabled: !hasPermissions || isDeleted,
        } as MenuAction;
      case ViolationsSupportedActions.ChatGptEnterpriseDeleteGptKnowledgeAction:
        hasPermissions =
          requiredPermissions && integrationId
            ? this._permissionsService.hasAnyRequiredPermission(requiredPermissions, integrationId)
            : true;
        return {
          name: ViolationsSupportedActions.ChatGptEnterpriseDeleteGptKnowledgeAction.toString(),
          displayNameLabel: 'commonActions.deleteResource',
          disabled: !hasPermissions || isDeleted,
        } as MenuAction;
      case ViolationsSupportedActions.ChatGptEnterpriseGetGptKnowledgeFileUrlAction:
        hasPermissions =
          requiredPermissions && integrationId
            ? this._permissionsService.hasAnyRequiredPermission(requiredPermissions, integrationId)
            : true;
        return {
          name: ViolationsSupportedActions.ChatGptEnterpriseGetGptKnowledgeFileUrlAction.toString(),
          displayNameLabel: 'commonActions.getGptKnowledge.title',
          disabled: !hasPermissions || isDeleted,
        } as MenuAction;
      case ViolationsSupportedActions.ChatGptEnterpriseDeleteUserCanvasAction:
        hasPermissions =
          requiredPermissions && integrationId
            ? this._permissionsService.hasAnyRequiredPermission(requiredPermissions, integrationId)
            : true;
        return {
          name: ViolationsSupportedActions.ChatGptEnterpriseDeleteUserCanvasAction.toString(),
          displayNameLabel: 'commonActions.deleteResource',
          disabled: !hasPermissions || isDeleted,
        } as MenuAction;
      case ViolationsSupportedActions.ChatGptEnterpriseGetUserCanvasAction:
        hasPermissions =
          requiredPermissions && integrationId
            ? this._permissionsService.hasAnyRequiredPermission(requiredPermissions, integrationId)
            : true;
        return {
          name: ViolationsSupportedActions.ChatGptEnterpriseDeleteUserCanvasAction.toString(),
          displayNameLabel: 'commonActions.deleteResource',
          disabled: !hasPermissions || isDeleted,
        } as MenuAction;
      default:
        throw new Error(`ViolationsActionsService getMenuAction: The action ${supportedAction} is not supported`);
    }
  }

  private _isResourceDeleted(inventoryResourceStatus?: ResourceStatus): boolean {
    return (
      inventoryResourceStatus != undefined &&
      inventoryResourceStatus != null &&
      inventoryResourceStatus === ResourceStatus.pendingDeletion
    );
  }

  private _createEmailData(violation: ViolationModel): EmailData {
    const resourceMakerEmails = this._commonActionsService.getResourceMakerEmails(
      violation.resource.identities?.ownedBy,
      violation.resource.identities?.createdBy
    );
    const platformDisplayName = getPlatformDisplayName(violation.resource.platformInfo.platform);
    const platformTypeDisplayName = beautifyCamelCaseString(violation.resource.resourceType);
    const platformLink = violation.resource.platformInfo?.urls.value?.webAppUrl?.value ?? 'N/A';

    let fixSuggestionText = '';
    if (violation.rule.riskReason) {
      fixSuggestionText += `Why is this a risk?
${violation.rule.riskReason
  .replace(/\r?\t/, '')
  .split('\n')
  .map((str) => str.trim())
  .join('\n')}\n\n`;
    }
    fixSuggestionText += `How to triage?
${violation.rule.triageSuggestion
  .replace(/\r?\t/, '')
  .split('\n')
  .map((str) => '* ' + str.trim())
  .join('\n')}`;
    if (violation.rule.fixSuggestion) {
      fixSuggestionText += `

How to fix?
${violation.rule.fixSuggestion
  .replace(/\r?\t/, '')
  .split('\n')
  .map((str) => str.trim())
  .join('\n')}`;
    }

    const to = '';
    const subject = `Violation found - ${violation.rule.displayName}`;
    const body = `Hi,

This is an automated email notifying you about a security violation recently found in ${platformDisplayName}.

* The Violation Impacts "${violation.resource.displayName}" which is a ${platformTypeDisplayName} in ${platformDisplayName}.
* The security risk has been rated as ${violation.rule.severity} risk and was first seen at ${formatFromDate(violation.firstSeen)}.

${fixSuggestionText}

Helpful links:
* Violation on Zenity: ${environment.app_base_url}/app/violations?${QueryParams.integration}=${violation.integration.id}&${QueryParams.violation}=${violation.id}

* ${platformDisplayName} ${platformTypeDisplayName} resource: ${platformLink}

Please act swiftly to mitigate the violation found and remove the risk.
For more information or questions please reach out to us at support@zenity.io`;

    return {
      to: to,
      subject: subject,
      body: body,
      integrationId: violation.integration.id,
      violationId: violation.id,
      resourceId: violation.resource.id,
      resourceMakerEmails: resourceMakerEmails,
      emailPlaceholders: this.getViolationEmailPlaceholders(),
    } as EmailData;
  }

  private _openChangeEventJustificationDialog(): Observable<IChangeEventJustificationComponentOutput> {
    const dialogRef = this._matDialog.open(ChangeEventJustificationDialogComponent, {
      disableClose: false,
      panelClass: 'sized-popup-dialog-container',
    });

    return dialogRef.afterClosed().pipe(map((output) => output || { addJustification: false }));
  }
}
